import React, { createContext, useContext, useState, useEffect } from 'react';
import useMinimumLoading from '../hooks/useMinimumLoading';
import { isTokenExpired } from '../utils/jwt';
import socketClient from '../utils/socketClient';
import pushNotificationService from '../utils/pushNotifications';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  /*
    Dynamic Permissions Flow:
    - JWT includes a snapshot of permissions at login.
    - When an admin updates a role's permissions on the backend, the server emits a
      'permissions_updated' socket event to every user with that role (room: user_<id>).
    - The socket client listens for 'permissions_updated' and re-emits internally.
    - This provider subscribes to that internal event; on receipt it calls refreshToken()
      which requests a fresh JWT via /api/auth/refresh-token (protected route) using
      current user id, embedding the new permission set.
    - The token in state/localStorage is replaced, causing hooks (usePermissions) to
      recompute and immediately update gated UI without requiring a manual logout/login.
    - If refresh fails, the old token remains and user keeps previous permissions until
      next successful refresh or login.
  */
  const [user, setUser] = useState(null);
  const [actualLoading, setActualLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  
  // Use minimum loading time of 500ms
  const loading = useMinimumLoading(actualLoading, 500);

  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        // First check if token is expired
        if (isTokenExpired(token)) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          setActualLoading(false);
          return;
        }

        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const userData = await response.json();
            if (userData.success) {
              setUser(userData.user);
              // Initialize socket connection after successful auth
              socketClient.connect(token);
            } else {
              // Invalid response structure
              localStorage.removeItem('token');
              setToken(null);
              setUser(null);
            }
          } else {
            // Token is invalid or expired
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setActualLoading(false);
    };

    initializeAuth();
  }, [token]);

  // Initialize push notifications when user is authenticated
  useEffect(() => {
    if (!user) return;
    // Request permission and subscribe (non-blocking)
    pushNotificationService.initialize().then((alreadySubscribed) => {
      if (!alreadySubscribed) {
        pushNotificationService.subscribe().catch(() => {});
      }
    }).catch(() => {});
  }, [user]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.user && data.user.emailVerified === false) {
          return { success: false, error: 'Please verify your email before logging in.', code: 'EMAIL_NOT_VERIFIED', email: data.user.email };
        }
        setToken(data.token);
        setUser(data.user);
        localStorage.setItem('token', data.token);
        // Initialize socket connection after successful login
        socketClient.connect(data.token);
        // Initialize push notifications (non-blocking)
        pushNotificationService.initialize().then(() => {
          pushNotificationService.subscribe().catch(() => {});
        }).catch(() => {});
        return { success: true };
      } else {
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  // Add verifyEmail for OTP
  const verifyEmail = async (email, otp) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        await refreshUser();
        return { success: true };
      } else {
        return { success: false, error: data.message || 'Invalid code' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        const msg = data.message || 'Registration failed';
        if (/invalid email/i.test(msg)) {
          return { success: false, error: msg, code: 'INVALID_EMAIL' };
        }
        return { success: false, error: msg };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    // Unsubscribe from push notifications (non-blocking)
    pushNotificationService.unsubscribe().catch(() => {});
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    // Clean up any cached data that might exist
    localStorage.removeItem('userProfile');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('userDataTimestamp');
    // Disconnect socket
    socketClient.disconnect();
  };

  const forgotPassword = async (email) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Request failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.message || 'Reset failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const refreshUser = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.success) {
          setUser(userData.user);
          return userData.user;
        }
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  // Refresh token to pick up updated permissions (invoked on permissions_updated socket event)
  const refreshToken = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success && data.token) {
        setToken(data.token);
        localStorage.setItem('token', data.token);
        await refreshUser();
      }
    } catch (e) {
      console.error('Token refresh failed:', e);
    }
  };

  // Listen to socket permissions updates to refresh token/permissions
  useEffect(() => {
    const handler = () => {
      refreshToken();
    };
    socketClient.on('permissions_updated', handler);
    return () => {
      socketClient.off('permissions_updated', handler);
    };
  }, [token]);

  const value = {
    user,
    userProfile: user, // Simplified - just use user directly
    token,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshUser,
    refreshToken,
    verifyEmail,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
